import React, { useEffect, useState } from 'react';
import './terms.css';
import usePageMeta from "../../../hooks/usePageMeta";

const generalTerms = [
  {
    number: 1,
    title: 'About MakeBeeTrip',
    paragraphs: [
      <>
        <strong>Legal Name:</strong> MAKEBEETRIP PRIVATE LIMITED
      </>,
      <>
        <strong>CIN:</strong> U79110DC2026PTC471178
      </>,
      <>
        <strong>Website:</strong>{' '}
        <a
          href="https://makebeetrip.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://makebeetrip.com
        </a>
      </>,
      <>
        <strong>Phone:</strong> 9355471999
      </>,
      <>
        <strong>Email:</strong>{' '}
        <a href="mailto:Holidays@makebeetrip.com">
          Holidays@makebeetrip.com
        </a>
      </>,
    ],
    address: (
      <>
        <strong>Registered/Mailing Address:</strong>
        <br />
        WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi – 110018,
        Delhi, India
      </>
    ),
    after: (
      <>
        MakeBeeTrip is a <strong>B2C travel platform</strong> offering
        travel-related services including:
      </>
    ),
    bullets: [
      'Flight Tickets',
      'Hotel Bookings',
      'Holiday Packages',
      'Cab & Transfers',
      'Bus Bookings',
      'Visa Assistance',
    ],
  },
  {
    number: 2,
    title: 'Use of the Website',
    paragraphs: [
      'You agree to use the MakeBeeTrip website only for lawful purposes.',
      'You must not:',
    ],
    bullets: [
      'Use the website for fraudulent or unlawful activities.',
      'Provide false, inaccurate or misleading information.',
      'Attempt to gain unauthorized access to the website or its systems.',
      'Interfere with the operation or security of the website.',
      'Use the website to distribute malicious software or harmful content.',
      "Make bookings using unauthorized payment methods or another person's payment information.",
    ],
    after: [
      'MakeBeeTrip reserves the right to restrict or terminate access where misuse or suspicious activity is identified.',
    ],
  },
  {
    number: 3,
    title: 'Travel Bookings',
    paragraphs: [
      'When you make a booking through MakeBeeTrip, you agree to provide accurate and complete information.',
      'This may include:',
    ],
    bullets: [
      'Passenger names',
      'Contact details',
      'Travel dates',
      'Date of birth',
      'Passport information',
      'Visa information',
      'Other information required for the selected travel service',
    ],
    after: [
      'Customers are responsible for checking the accuracy of all information before confirming a booking.',
      "Incorrect information may result in additional charges, amendments, cancellation or denial of travel services depending on the applicable supplier's rules.",
    ],
  },
  {
    number: 4,
    title: 'Booking Confirmation',
    paragraphs: [
      'A booking is considered confirmed only after the applicable travel service provider or supplier has confirmed the booking and MakeBeeTrip has issued the relevant confirmation.',
      'Payment alone does not necessarily guarantee final booking confirmation.',
      'Booking confirmation may be subject to:',
    ],
    bullets: [
      'Availability',
      'Airline or hotel confirmation',
      'Supplier approval',
      'Payment verification',
      'Applicable fare or service conditions',
    ],
  },
  {
    number: 5,
    title: 'Prices and Availability',
    paragraphs: [
      'All prices displayed on the website are subject to availability and may change without prior notice.',
      'Travel prices may vary due to:',
    ],
    bullets: [
      'Airline fare changes',
      'Hotel availability',
      'Currency fluctuations',
      'Taxes and government charges',
      'Supplier pricing',
      'Seasonal demand',
      'Changes in travel dates or passenger details',
    ],
    after: [
      'The final applicable price will be communicated or displayed at the time of booking.',
    ],
  },
  {
    number: 6,
    title: 'Flight Bookings',
    paragraphs: [
      "Flight bookings are subject to the applicable airline's:",
    ],
    bullets: [
      'Fare rules',
      'Baggage allowance',
      'Cancellation policy',
      'Date-change policy',
      'No-show policy',
      'Refund rules',
      'Schedule and operational conditions',
    ],
    after: [
      'Flight schedules may be changed, delayed or cancelled by airlines.',
      "MakeBeeTrip may assist customers with available options, but the airline's applicable terms and operational decisions will govern the flight booking.",
    ],
  },
  {
    number: 7,
    title: 'Hotel Bookings',
    paragraphs: [
      "Hotel bookings are subject to the hotel's applicable:",
    ],
    bullets: [
      'Check-in and check-out policies',
      'Cancellation policy',
      'Refund policy',
      'Child policy',
      'Meal plan',
      'Room availability',
      'Extra-bed policy',
      'Taxes and charges',
    ],
    after: [
      'Hotel images and descriptions may be provided by hotels, suppliers or other travel partners and may be subject to change.',
    ],
  },
  {
    number: 8,
    title: 'Holiday Packages',
    paragraphs: [
      'Holiday packages may include different combinations of hotels, transportation, sightseeing, meals, activities and other services.',
      'The exact inclusions and exclusions applicable to a package will be mentioned in the relevant quotation, itinerary or booking confirmation.',
      'Changes to itinerary, hotel, transportation or sightseeing may be required due to availability, weather, operational conditions, local restrictions or supplier requirements.',
    ],
  },
  {
    number: 9,
    title: 'Cab and Transportation Services',
    paragraphs: [
      'Cab and transportation services are subject to the terms of the applicable transportation provider.',
      'Vehicle type, availability, route, pickup time and other transportation details may be subject to operational conditions.',
      'Additional charges may apply for services outside the agreed itinerary or inclusions.',
    ],
  },
  {
    number: 10,
    title: 'Bus Bookings',
    paragraphs: [
      "Bus bookings are subject to the applicable bus operator's terms, including:",
    ],
    bullets: [
      'Cancellation rules',
      'Rescheduling rules',
      'Boarding requirements',
      'Luggage policies',
      'Route and schedule changes',
    ],
    after: [
      'The bus operator may modify schedules or services due to operational circumstances.',
    ],
  },
  {
    number: 11,
    title: 'Visa Assistance',
    paragraphs: [
      'MakeBeeTrip may provide visa assistance and documentation support for selected destinations.',
      'Visa approval is solely at the discretion of the relevant embassy, consulate, immigration authority or visa-issuing authority.',
      'MakeBeeTrip does not guarantee visa approval.',
      'Visa fees, service fees and other charges may be non-refundable depending on the applicable service terms.',
      'Customers are responsible for providing genuine, complete and accurate documents.',
    ],
  },
  {
    number: 12,
    title: 'Passport and Travel Documents',
    paragraphs: [
      'For international travel, customers are responsible for ensuring that they have valid:',
    ],
    bullets: [
      'Passport',
      'Visa, where required',
      'Travel documents',
      'Entry permits',
      'Required certificates or documents',
    ],
    after: [
      'Passport validity and entry requirements may differ by destination.',
      'Customers should verify the applicable requirements before travelling.',
    ],
  },
  {
    number: 13,
    title: 'Payment',
    paragraphs: [
      'Payments may be processed through third-party payment gateway providers.',
      'Customers agree to provide valid payment information and authorize the applicable payment transaction for the selected service.',
      'MakeBeeTrip does not ask customers to provide confidential authentication details such as UPI PIN, card PIN, banking passwords or payment OTPs.',
      <>
        Please refer to our{' '}
        <a href="/payment-security">
          <strong>Payment Security</strong>
        </a>{' '}
        page for additional information.
      </>,
    ],
  },
  {
    number: 14,
    title: 'Cancellation and Refunds',
    paragraphs: [
      'Cancellation and refund conditions depend on the specific travel service booked.',
      'Different airlines, hotels, transportation providers, visa service providers and holiday suppliers may have different cancellation and refund policies.',
      'Any applicable cancellation charges, supplier penalties, service fees, taxes or other deductions may be applied before processing a refund.',
      'Customers should review the applicable cancellation and refund conditions before confirming their booking.',
    ],
  },
  {
    number: 15,
    title: 'Travel Supplier Terms',
    paragraphs: [
      'MakeBeeTrip works with airlines, hotels, transportation providers, tour operators, visa service providers, payment providers and other travel suppliers.',
      "The services purchased through MakeBeeTrip may also be subject to the respective supplier's terms and conditions.",
      'Where supplier-specific terms apply, those terms may govern matters such as:',
    ],
    bullets: [
      'Cancellation',
      'Refunds',
      'Rescheduling',
      'Baggage',
      'Hotel policies',
      'Visa processing',
      'Transportation',
      'No-show',
      'Service changes',
    ],
  },
  {
    number: 16,
    title: 'Changes to Bookings',
    paragraphs: [
      "Any request to modify a booking is subject to availability and the applicable supplier's rules.",
      'Additional charges may apply for:',
    ],
    bullets: [
      'Date changes',
      'Name corrections',
      'Route changes',
      'Hotel changes',
      'Room upgrades',
      'Transportation changes',
      'Other amendments',
    ],
    after: [
      'MakeBeeTrip will communicate applicable charges where available.',
    ],
  },
  {
    number: 17,
    title: 'Customer Responsibilities',
    paragraphs: ['Customers are responsible for:'],
    bullets: [
      'Providing accurate information.',
      'Checking booking details before confirmation.',
      'Maintaining valid travel documents.',
      'Arriving at airports, hotels, boarding points and pickup locations on time.',
      'Following applicable airline, hotel, transport and destination rules.',
      'Checking visa and entry requirements.',
      'Keeping booking confirmations and travel documents safely.',
    ],
  },
  {
    number: 18,
    title: 'Website Information',
    paragraphs: [
      'We make reasonable efforts to keep information on the website accurate and updated.',
      'However, travel information, prices, availability, schedules and supplier policies may change.',
      'MakeBeeTrip does not guarantee that every website detail will always be complete, current or error-free.',
    ],
  },
  {
    number: 19,
    title: 'Third-Party Links',
    paragraphs: [
      'The MakeBeeTrip website may contain links to third-party websites or services.',
      'These may include airline, hotel, payment, visa, transportation or other travel service providers.',
      'MakeBeeTrip is not responsible for the content, availability, privacy practices or policies of third-party websites.',
    ],
  },
  {
    number: 20,
    title: 'Limitation of Liability',
    paragraphs: [
      'MakeBeeTrip acts as a travel platform and booking facilitator for various travel services.',
      'To the extent permitted by applicable law, MakeBeeTrip shall not be responsible for circumstances outside its reasonable control, including:',
    ],
    bullets: [
      'Airline delays or cancellations',
      'Flight schedule changes',
      'Hotel operational issues',
      'Transportation delays',
      'Weather conditions',
      'Natural disasters',
      'Government restrictions',
      'Immigration or visa decisions',
      'Strikes or operational disruptions',
      'Supplier failures',
      'Events beyond reasonable control',
    ],
    after: [
      "Any claims relating to a specific travel service may also be subject to the applicable supplier's terms and conditions.",
    ],
  },
  {
    number: 21,
    title: 'Force Majeure',
    paragraphs: [
      'MakeBeeTrip shall not be responsible for failure or delay in providing services where such failure or delay results from circumstances beyond reasonable control.',
      'Such circumstances may include natural disasters, pandemics, government restrictions, war, civil disturbances, strikes, technical failures, transportation disruptions or other unforeseen events.',
    ],
  },
  {
    number: 22,
    title: 'Intellectual Property',
    paragraphs: [
      'The content of the MakeBeeTrip website, including text, graphics, logos, designs, images, trademarks and other materials, may be protected by applicable intellectual property laws.',
      'You may not reproduce, copy, modify, distribute or commercially use website content without prior written permission.',
    ],
  },
  {
    number: 23,
    title: 'Privacy',
    paragraphs: [
      <>
        Your use of the MakeBeeTrip website is also subject to our{' '}
        <a href="/privacy">
          <strong>Privacy Policy</strong>
        </a>
        .
      </>,
      'Our Privacy Policy explains how personal information may be collected, used, processed and shared.',
    ],
  },
  {
    number: 24,
    title: 'Changes to These Terms',
    paragraphs: [
      'MakeBeeTrip may update these Terms & Conditions from time to time.',
      'Updated Terms will be published on this page with a revised "Last Updated" date.',
      'Your continued use of the website after an update may constitute acceptance of the revised Terms, subject to applicable law.',
    ],
  },
  {
    number: 25,
    title: 'Governing Law',
    paragraphs: [
      'These Terms & Conditions shall be governed by and interpreted in accordance with the applicable laws of India.',
      'Any dispute relating to the use of the website or services provided by MakeBeeTrip shall be subject to the jurisdiction of the competent courts in Delhi, India, subject to applicable law.',
    ],
  },
  {
    number: 26,
    title: 'Contact Us',
    paragraphs: [
      'For any questions regarding these Terms & Conditions, bookings or travel services, please contact:',
      <strong>MakeBeeTrip Private Limited</strong>,
      <>
        <strong>Phone:</strong> 9355471999
      </>,
      <>
        <strong>Email:</strong>{' '}
        <a href="mailto:Holidays@makebeetrip.com">
          Holidays@makebeetrip.com
        </a>
      </>,
      <>
        <strong>Website:</strong>{' '}
        <a
          href="https://makebeetrip.com"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://makebeetrip.com
        </a>
      </>,
    ],
    address: (
      <>
        <strong>Registered/Mailing Address:</strong>
        <br />
        WZ A-1/1, G/F R/P, Vikas Puri, New Delhi, West Delhi – 110018,
        Delhi, India
      </>
    ),
    after: [
      <>
        <strong>CIN:</strong> U79110DC2026PTC471178
      </>,
    ],
  },
];

function TermsSection({
  section,
  idPrefix = 'general-section',
}) {
  return (
    <section
      className="mbt-legal-section"
      id={`${idPrefix}-${section.number}`}
    >
      <div className="mbt-section-heading">
        <span className="mbt-section-number">
          {String(section.number).padStart(2, '0')}
        </span>

        <h2>{section.title}</h2>
      </div>

      {section.paragraphs?.map((paragraph, index) => (
        <p key={`p-${section.number}-${index}`}>
          {paragraph}
        </p>
      ))}

      {section.emphasis && (
        <div className="mbt-important-inline">
          {section.emphasis}
        </div>
      )}

      {section.address && (
        <div className="mbt-address-block">
          {section.address}
        </div>
      )}

      {section.bullets && (
  <ul className="mbt-legal-list">
    {section.bullets.map((item, index) => (
      <li key={`b-${section.number}-${index}`}>{item}</li>
    ))}
  </ul>
)}

{section.after && (
  <>
    {Array.isArray(section.after) ? (
      section.after.map((paragraph, index) => (
        <p key={`after-${section.number}-${index}`}>
          {paragraph}
        </p>
      ))
    ) : (
      <p>{section.after}</p>
    )}
  </>
)}
    </section>
  );
}

export default function LegalTerms() {
  usePageMeta(
  "Terms & Conditions | MakeBeeTrip",
  "Read the terms and conditions governing bookings, payments, cancellations and use of MakeBeeTrip's travel services, website and mobile application.",
  "makebeetrip terms and conditions, booking terms, cancellation terms, user agreement, travel terms, service conditions"
);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight =
        document.documentElement.scrollHeight - window.innerHeight;

      setScrollProgress(
        totalHeight > 0
          ? Math.min((window.scrollY / totalHeight) * 100, 100)
          : 0
      );
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    <div className="mbt-legal-wrapper">
      <div
        className="mbt-scroll-indicator"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      {/* Sticky navigation */}
      <div className="mbt-sticky-nav">
        <div className="mbt-nav-container">
          <button
            onClick={() => window.history.back()}
            className="mbt-back-button"
            aria-label="Go Back"
            type="button"
          >
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>

            <span>Back</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="mbt-legal-hero">
        <div className="mbt-hero-container">
          <div className="mbt-legal-icon-box" aria-hidden="true">
            <svg
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
              />
            </svg>
          </div>

          <span className="mbt-hero-eyebrow">GENERAL TERMS</span>

          <h1>Terms &amp; Conditions</h1>

          <p className="mbt-hero-desc">
            Please read these Terms &amp; Conditions carefully before using
            the MakeBeeTrip website, submitting an enquiry or making a booking.
          </p>

          <div className="mbt-updated-badge">
            Last Updated: 24 September 2026
          </div>
        </div>
      </section>

      <main className="mbt-content-container">
        <article className="mbt-legal-card">
          {/* Introduction */}
          <div className="mbt-document-intro">
            <p>
              These Terms &amp; Conditions ("Terms", "Terms &amp; Conditions")
              govern your access to and use of the <strong>MakeBeeTrip</strong>{' '}
              website and the travel services provided through the platform.
            </p>

            <p>
              By accessing{' '}
              <a
                href="https://makebeetrip.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <strong>https://makebeetrip.com</strong>
              </a>
              , submitting an enquiry, making a booking or using any service
              provided by MakeBeeTrip, you acknowledge that you have read,
              understood and agreed to these Terms &amp; Conditions.
            </p>
          </div>

          {/* General Terms */}
          <div id="general-terms" className="mbt-document-section">
            <div className="mbt-document-section__header">
              <span>01</span>
              <div>
                <p>PRIMARY DOCUMENT</p>
                <h2>General Terms &amp; Conditions</h2>
              </div>
            </div>

            {generalTerms.map((section, index) => (
              <React.Fragment key={section.number}>
                <TermsSection section={section} />

                {index < generalTerms.length - 1 && (
                  <hr className="mbt-divider" />
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Closing */}
          <div className="mbt-legal-closing">
            MakeBeeTrip – Your Journey, Our Commitment.
          </div>
        </article>
      </main>

      {/* Footer */}
      <footer className="mbt-legal-footer">
        <div className="mbt-footer-inner">
          <nav className="mbt-footer-nav" aria-label="Legal navigation">
            <a href="/terms">General Terms</a>
            <a href="/privacy">Privacy Policy</a>
            <a href="/payment-security">Payment Security</a>
            <a href="/cancellation-refund">Cancellation &amp; Refund</a>
            <a href="/about">About Us</a>
            <a href="/contact">Contact Us</a>
          </nav>

          <p className="mbt-copyright">
            © 2026 MAKEBEETRIP PRIVATE LIMITED. All rights reserved.
          </p>
        </div>
      </footer>

      <button
        type="button"
        className="mbt-scroll-top"
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        ↑
      </button>
    </div>
  );
}


